import { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, isToday, isThisWeek } from 'date-fns';
import { Edit, Trash2, Plus, CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePagination } from '@/hooks/usePagination';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import type { CRMStakeholder, CRMInteraction, CRMFollowUp, InteractionType } from '@/lib/crm/types';
import { getInteractionTypeColor } from '@/lib/stakeholders/utils';
import { EventFilesCell } from './EventFilesCell';

interface EventsTableProps {
  stakeholders: CRMStakeholder[];
  interactions: CRMInteraction[];
  followUps: CRMFollowUp[];
  onEditInteraction: (interaction: CRMInteraction) => void;
  onEditFollowUp: (followUp: CRMFollowUp) => void;
  onDeleteInteraction: (id: string) => void;
  onDeleteFollowUp: (id: string) => void;
  onAddEvent?: () => void;
  canEdit: boolean;
  canDelete: boolean;
  canCreate?: boolean;
}

type CombinedEvent = {
  id: string;
  type: 'interaction' | 'follow-up';
  eventType: string;
  stakeholder: CRMStakeholder | undefined;
  date: string;
  content: string;
  outcome?: string | null;
  attendees?: string | null;
  notes?: string | null;
  status?: string | null;
  rawInteraction?: CRMInteraction;
  rawFollowUp?: CRMFollowUp;
  stakeholderName?: string;
  subject?: string;
};

/**
 * Generate .ics file content for calendar import
 */
const generateIcsFile = (event: {
  date: string;
  type: string;
  subject?: string;
  notes?: string;
  stakeholderName?: string;
}): string => {
  const startDate = new Date(event.date);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hour
  
  const formatIcsDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };
  
  const uid = `crm-event-${event.type}-${Date.now()}@yourdomain.com`;
  const summary = `${event.type}: ${event.subject || 'CRM Event'}`;
  const description = (event.notes || '').replace(/\n/g, '\\n');
  const location = event.stakeholderName || '';
  
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CRM System//Event//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(startDate)}`,
    `DTEND:${formatIcsDate(endDate)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

/**
 * Download .ics file for calendar import
 */
const downloadIcsFile = (event: {
  date: string;
  type: string;
  subject?: string;
  notes?: string;
  stakeholderName?: string;
}) => {
  const icsContent = generateIcsFile(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `event-${event.type}-${Date.now()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export function EventsTable({
  stakeholders,
  interactions,
  followUps,
  onEditInteraction,
  onEditFollowUp,
  onDeleteInteraction,
  onDeleteFollowUp,
  onAddEvent,
  canEdit,
  canDelete,
  canCreate = false
}: EventsTableProps) {
  const now = new Date();
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [stakeholderFilter, setStakeholderFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>(format(startOfMonth(now), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState<string>(format(endOfMonth(now), 'yyyy-MM-dd'));
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Combine events
  const allEvents = useMemo((): CombinedEvent[] => {
    const events: CombinedEvent[] = [];

    // Add interactions
    interactions.forEach(i => {
      const stakeholder = stakeholders.find(s => s.id === i.stakeholder_id);
      events.push({
        id: i.id,
        type: 'interaction',
        eventType: i.type,
        stakeholder,
        stakeholderName: stakeholder?.name,
        date: i.date,
        content: i.subject,
        subject: i.subject,
        outcome: i.outcome,
        attendees: i.attendees,
        notes: i.notes,
        status: i.status,
        rawInteraction: i
      });
    });

    // Add follow-ups
    followUps.forEach(f => {
      const stakeholder = stakeholders.find(s => s.id === f.stakeholder_id);
      events.push({
        id: f.id,
        type: 'follow-up',
        eventType: f.type || 'Follow-up',
        stakeholder,
        stakeholderName: stakeholder?.name,
        date: f.follow_up_date,
        content: f.subject || f.notes || 'Không có ghi chú',
        subject: f.subject,
        outcome: f.outcome,
        attendees: f.attendees,
        notes: f.notes,
        status: f.status,
        rawFollowUp: f
      });
    });

    return events;
  }, [stakeholders, interactions, followUps]);

  // Filter events
  const filteredEvents = useMemo(() => {
    let filtered = [...allEvents];

    // Filter by event type
    if (eventTypeFilter !== 'all') {
      if (eventTypeFilter === 'interactions') {
        filtered = filtered.filter(e => e.type === 'interaction');
      } else if (eventTypeFilter === 'follow-ups') {
        filtered = filtered.filter(e => e.type === 'follow-up');
      }
    }

    // Filter by stakeholder
    if (stakeholderFilter !== 'all') {
      filtered = filtered.filter(e => e.stakeholder?.id === stakeholderFilter);
    }

    // Filter by date range
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(e => new Date(e.date) >= fromDate);
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(e => new Date(e.date) <= toDate);
    }

    // Filter by status (for follow-ups)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    return filtered;
  }, [allEvents, eventTypeFilter, stakeholderFilter, dateFrom, dateTo, statusFilter]);

  // Sort events
  const sortedEvents = useMemo(() => {
    const sorted = [...filteredEvents];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'eventType':
          comparison = a.eventType.localeCompare(b.eventType);
          break;
        case 'stakeholder':
          const nameA = a.stakeholder?.name || '';
          const nameB = b.stakeholder?.name || '';
          comparison = nameA.localeCompare(nameB);
          break;
        case 'status':
          const statusA = a.status || '';
          const statusB = b.status || '';
          comparison = statusA.localeCompare(statusB);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredEvents, sortColumn, sortDirection]);

  const pagination = usePagination(sortedEvents.length, { pageSize: 25, initialPage: 1 });
  const { currentPage, totalPages, startItem, endItem, setPage, hasNextPage, hasPreviousPage } = pagination;

  const paginatedEvents = useMemo(() => {
    return sortedEvents.slice(startItem - 1, endItem);
  }, [sortedEvents, startItem, endItem]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const clearFilters = () => {
    const now = new Date();
    setEventTypeFilter('all');
    setStakeholderFilter('all');
    setDateFrom(format(startOfMonth(now), 'yyyy-MM-dd'));
    setDateTo(format(endOfMonth(now), 'yyyy-MM-dd'));
    setStatusFilter('all');
  };

  const getEventBadgeClass = (event: CombinedEvent) => {
    return getInteractionTypeColor(event.eventType as InteractionType);
  };

  const getRowHighlight = (eventDate: string): string => {
    const date = new Date(eventDate);
    
    if (isToday(date)) {
      return 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/30 dark:hover:bg-orange-950/50';
    }
    
    if (isThisWeek(date, { weekStartsOn: 1 })) {
      return 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-950/30 dark:hover:bg-yellow-950/50';
    }
    
    return 'hover:bg-muted/50';
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const handleEdit = (event: CombinedEvent) => {
    if (!canEdit) return;
    if (event.type === 'interaction' && event.rawInteraction) {
      onEditInteraction(event.rawInteraction);
    } else if (event.type === 'follow-up' && event.rawFollowUp) {
      onEditFollowUp(event.rawFollowUp);
    }
  };

  const handleDelete = (event: CombinedEvent) => {
    if (!canDelete) return;
    if (event.type === 'interaction') {
      onDeleteInteraction(event.id);
    } else {
      onDeleteFollowUp(event.id);
    }
  };

  return (
    <div className="space-y-4">
      {/* Add Event Button */}
      {canCreate && (
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Events</h2>
          <Button onClick={onAddEvent} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </Button>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
        <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="interactions">Interactions Only</SelectItem>
            <SelectItem value="follow-ups">Follow-ups Only</SelectItem>
          </SelectContent>
        </Select>

        <Select value={stakeholderFilter} onValueChange={setStakeholderFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Stakeholder" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stakeholders</SelectItem>
            {stakeholders.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          placeholder="From"
          className="w-full sm:w-[150px]"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          placeholder="To"
          className="w-full sm:w-[150px]"
        />

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={clearFilters} className="w-full sm:w-auto">
          Clear Filters
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
        {sortedEvents.length === 0 ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-muted-foreground">Chưa có sự kiện nào</div>
          </div>
        ) : (
          <>
              <Table className="min-w-[1200px]">
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="w-[140px] cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('eventType')}
                    >
                      Type {sortColumn === 'eventType' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="w-[180px] cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('stakeholder')}
                    >
                      Stakeholder {sortColumn === 'stakeholder' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="w-[170px] cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortColumn === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead className="w-[150px]">Project</TableHead>
                    <TableHead className="flex-1 min-w-[200px]">Subject/Notes</TableHead>
                    <TableHead className="w-[150px]">Outcome</TableHead>
                    <TableHead className="w-[150px]">Attendees</TableHead>
                    <TableHead 
                      className="w-[120px] cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('status')}
                    >
                      Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead className="w-[120px]">Files</TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead className="w-[100px] text-center">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEvents.map((event) => (
                    <TableRow 
                      key={event.id}
                      className={`${getRowHighlight(event.date)} cursor-pointer`}
                      onDoubleClick={() => handleEdit(event)}
                    >
                      <TableCell>
                        <div className="relative inline-block">
                          <Badge className={getEventBadgeClass(event)}>
                            {event.eventType}
                          </Badge>
                          <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                            {event.type === 'interaction' ? 'I' : 'F'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {event.stakeholder?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {format(new Date(event.date), 'dd/MM/yyyy')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(event.date), 'HH:mm')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={event.stakeholder?.project ? '' : 'text-muted-foreground italic'}>
                          {event.stakeholder?.project || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md truncate" title={event.content}>
                          {event.content}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[150px] truncate" title={event.outcome || ''}>
                          {event.outcome || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[150px] truncate" title={event.attendees || ''}>
                          {event.attendees || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {event.status ? (
                          <Badge variant={getStatusVariant(event.status) as any}>
                            {event.status}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <EventFilesCell
                            eventType={event.type}
                            eventId={event.id}
                            canUpload={canEdit}
                          />
                          
                          {/* Calendar Export Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadIcsFile({
                                date: event.date,
                                type: event.eventType,
                                subject: event.subject,
                                notes: event.notes || undefined,
                                stakeholderName: event.stakeholderName,
                              });
                            }}
                            title="Tải file .ics để thêm vào Calendar"
                          >
                            <CalendarPlus className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      {(canEdit || canDelete) && (
                        <TableCell className="text-center">
                          <div className="flex gap-2 justify-center">
                            {canEdit && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-8 w-8 sm:w-auto px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(event);
                                }}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-8 w-8 sm:w-auto px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(event);
                                }}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-muted-foreground hidden sm:block">
                  Showing {startItem} to {endItem} of {sortedEvents.length} events
                </div>
                <div className="text-xs text-muted-foreground sm:hidden">
                  {currentPage}/{totalPages}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => hasPreviousPage && setPage(currentPage - 1)}
                        className={!hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => hasNextPage && setPage(currentPage + 1)}
                        className={!hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
